import { useEffect, useState } from 'react';
import { api } from '../api';
import { styles, colors, fmtDate, useIsMobile } from './UI';

// Tasks widget. Personal to-do list -- not tied to a call log entry. Lives on
// the Dashboard. Quick-add input at top, list below. Tap checkbox to mark
// done; completed tasks fade and sort to bottom.
//
// kind: when the parent Dashboard's Base/Project toggle is set, the widget
// filters the list strictly to that kind and defaults new tasks to it.
// Empty/null = "All" mode -- no filter, new tasks created with no kind.
//
// scope: 'mine' (default) shows only the caller's tasks; 'team' shows tasks
// for accessible reps (supervisor: their branch reps; admin: everyone).
// Owner name shows on each row when in team mode. The create form is hidden
// in team mode -- supervisors review their team's tasks but don't add to
// other people's lists from here.
export default function TasksWidget({ kind = '', scope = 'mine' } = {}) {
  const [tasks, setTasks] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [newDue, setNewDue] = useState('');
  // newKind tracks the current default for the create form. Reseeds from
  // the prop whenever the Dashboard kind toggle changes.
  const [newKind, setNewKind] = useState(kind || '');
  const [error, setError] = useState('');

  const reload = () => {
    const params = {};
    if (kind === 'Base' || kind === 'Project') params.kind = kind;
    if (scope === 'team') params.scope = 'team';
    return api.listTasks(params).then(setTasks).catch(e => setError(e.message));
  };
  useEffect(() => { reload(); }, [kind, scope]);
  // When the Dashboard's kind toggle flips, also flip the create form default
  // so a new task lands in the bucket the user is currently looking at.
  useEffect(() => { setNewKind(kind || ''); }, [kind]);

  const add = async (e) => {
    e?.preventDefault();
    if (!newTitle.trim()) return;
    try {
      await api.createTask({
        title: newTitle.trim(),
        due_date: newDue || null,
        kind: newKind || null
      });
      setNewTitle(''); setNewDue('');
      reload();
    } catch (err) { setError(err.message); }
  };

  const toggle = async (t) => {
    try {
      await api.updateTask(t.id, { completed: !t.completed });
      reload();
    } catch (err) { setError(err.message); }
  };

  // Cycle a task's kind: none -> Base -> Project -> none. One click on the
  // pill changes it. Lets users retag existing tasks without an edit form.
  const cycleKind = async (t) => {
    const next = t.kind === 'Base' ? 'Project' : t.kind === 'Project' ? null : 'Base';
    try {
      await api.updateTask(t.id, { kind: next });
      reload();
    } catch (err) { setError(err.message); }
  };

  const remove = async (id) => {
    if (!confirm('Delete this task?')) return;
    await api.deleteTask(id);
    reload();
  };

  const open = tasks.filter(t => !t.completed);
  const done = tasks.filter(t => t.completed);

  const isTeam = scope === 'team';

  return (
    <div style={styles.card}>
      <h2 style={styles.h2}>
        ✓ Tasks
        <span style={{ color: colors.textMuted, fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
          · {open.length} open{done.length > 0 && `, ${done.length} done`}
          {kind && ` · ${kind} only`}
          {isTeam && ' · Team'}
        </span>
      </h2>

      {error && <div style={styles.errorBox}>{error}</div>}

      {/* Create form -- hidden in team mode. A supervisor reviewing the
          team's queue shouldn't accidentally add to someone else's list
          from here; flip back to Mine to add personal tasks. */}
      {!isTeam && (
        <form onSubmit={add} style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <input
            style={{ ...styles.input, flex: 1, minWidth: 200 }}
            placeholder="What needs done? (e.g. send pricing to John)"
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
          />
          <input
            type="date"
            style={{ ...styles.input, width: 160 }}
            value={newDue}
            onChange={e => setNewDue(e.target.value)}
            title="Due date (optional)"
          />
          <select
            style={{ ...styles.input, width: 120 }}
            value={newKind}
            onChange={e => setNewKind(e.target.value)}
            title="Tag this task as Base or Project (optional)"
          >
            <option value="">No kind</option>
            <option value="Base">Base</option>
            <option value="Project">Project</option>
          </select>
          <button type="submit" style={styles.btn} disabled={!newTitle.trim()}>Add</button>
        </form>
      )}

      {open.length === 0 && done.length === 0 && (
        <p style={{ color: colors.textMuted, fontSize: 13, margin: 0 }}>
          {isTeam
            ? `No ${kind ? kind + ' ' : ''}team tasks open right now.`
            : (kind ? `No ${kind} tasks. Add one above.` : 'No tasks yet. Add one above.')}
        </p>
      )}

      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {open.map(t => (
          <TaskRow key={t.id} task={t} isTeam={isTeam} onToggle={() => toggle(t)} onCycleKind={() => cycleKind(t)} onDelete={() => remove(t.id)} />
        ))}
        {done.length > 0 && open.length > 0 && <li style={{ height: 8 }} />}
        {done.map(t => (
          <TaskRow key={t.id} task={t} isTeam={isTeam} onToggle={() => toggle(t)} onCycleKind={() => cycleKind(t)} onDelete={() => remove(t.id)} />
        ))}
      </ul>
    </div>
  );
}

const KIND_PILL = {
  Base:    { bg: '#F3F4F6', fg: '#374151', label: 'Base' },
  Project: { bg: '#EEF2FF', fg: '#3730A3', label: 'Project' }
};

const TaskRow = ({ task, isTeam, onToggle, onCycleKind, onDelete }) => {
  // On phones the single flex row squeezed the title to one word per line
  // (chips + date took the width), so the pills move to their own line
  // under the title. Desktop keeps the one-line layout.
  const isMobile = useIsMobile();
  const overdue = !task.completed && task.due_date && task.due_date.slice(0, 10) < new Date().toISOString().slice(0, 10);
  const pill = task.kind ? KIND_PILL[task.kind] : null;

  const kindPill = isTeam ? (
    pill && (
      <span style={{
        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
        background: pill.bg, color: pill.fg, whiteSpace: 'nowrap'
      }}>
        {pill.label}
      </span>
    )
  ) : (
    <button
      onClick={onCycleKind}
      title="Click to cycle Base / Project / none"
      style={{
        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
        background: pill ? pill.bg : 'transparent',
        color: pill ? pill.fg : colors.textMuted,
        border: pill ? 'none' : `1px dashed ${colors.border}`,
        cursor: 'pointer', whiteSpace: 'nowrap'
      }}
    >
      {pill ? pill.label : '+ kind'}
    </button>
  );

  const dueChip = task.due_date && (
    <span style={{
      fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
      color: overdue ? colors.red : colors.textMuted,
      background: overdue ? colors.redSoft : 'transparent',
      padding: '2px 8px', borderRadius: 8
    }}>
      {overdue ? 'OVERDUE · ' : ''}{fmtDate(task.due_date)}
    </span>
  );

  // Delete hidden in team mode -- supervisors don't trash team tasks
  const deleteBtn = !isTeam && (
    <button
      onClick={onDelete}
      style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: 16, padding: '0 4px' }}
      title="Delete"
    >×</button>
  );

  const checkbox = (
    <input
      type="checkbox"
      checked={task.completed}
      onChange={onToggle}
      // In team mode supervisors can see but not toggle others' tasks
      // (the server enforces -- a 404 would come back). Disable for safety.
      disabled={isTeam}
      style={{ width: isMobile ? 20 : 16, height: isMobile ? 20 : 16, cursor: isTeam ? 'not-allowed' : 'pointer', flexShrink: 0 }}
      title={isTeam ? 'Switch to Mine to mark your own tasks done' : ''}
    />
  );

  const title = (
    <span style={{
      flex: 1, fontSize: 14, minWidth: 0,
      textDecoration: task.completed ? 'line-through' : 'none',
      color: task.completed ? colors.textMuted : colors.text
    }}>
      {task.title}
      {isTeam && task.user_name && (
        <span style={{ marginLeft: 8, color: colors.textMuted, fontSize: 11, fontWeight: 600 }}>
          · {task.user_name}
        </span>
      )}
    </span>
  );

  if (isMobile) {
    return (
      <li style={{ padding: '10px 0', borderBottom: `1px solid ${colors.border}`, opacity: task.completed ? 0.5 : 1 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          {checkbox}
          {title}
        </div>
        {(kindPill || dueChip || deleteBtn) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, paddingLeft: 30, flexWrap: 'wrap' }}>
            {kindPill}
            {dueChip}
            <span style={{ flex: 1 }} />
            {deleteBtn}
          </div>
        )}
      </li>
    );
  }

  return (
    <li style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
      borderBottom: `1px solid ${colors.border}`, opacity: task.completed ? 0.5 : 1
    }}>
      {checkbox}
      {title}
      {/* Kind pill -- click to cycle in mine mode, read-only badge in team mode */}
      {kindPill}
      {dueChip}
      {deleteBtn}
    </li>
  );
};
